// Agreement-consent versioning + helpers for the account-level documents the
// user accepts at sign-up (Terms / Privacy / AI Notice). These are stored as a
// versioned JSON blob in the Cognito `custom:consents` attribute, written at
// sign-up via `buildConsentPayload()` and revoked/re-granted from the Account
// page via the helpers below.
//
// NOTE: this is the AGREEMENT-consent layer. The separate TRACKING-consent layer
// (cookies / ad-tech / analytics) is owned by the Termly CMP under
// `src/consent/` and is NEVER stored here. See `docs/privacy-consent.md`.

export const CONSENT_VERSIONS = {
  // Bumped v5 -> v6 (2026-08-06): dropped the named "Google AdSense" example
  // from the advertising clause — the AdSense integration was removed and no
  // ad network is currently wired. Narrows disclosure; no new rights or data
  // uses, so no re-prompt.
  // v4 -> v5 (2026-07-25): operator identified as "Andrew Paris LLC,
  // d/b/a MediaShare-Link" (the LLC's registered DBA, matching the EIN record;
  // required for SMS A2P vetting). No substantive rights changes.
  // v3 -> v4 (2026-07-19): guest acceptance-by-use + ad-supported disclosure.
  // v2 -> v3 (2026-07-08): "Subscriptions, Fees, and Billing" section.
  tos: 'v6',
  // Bumped v4 -> v5 (2026-08-06): removed every named Google ad property
  // (AdSense, Google Ads, Google Analytics — none of which are in use; site
  // analytics is CloudWatch RUM) and the Google ad-settings opt-out link, and
  // softened present-tense "we display / we share" to "may", since no ad
  // network is currently wired. Strictly narrows disclosure, so no re-prompt.
  // v3 -> v4 (2026-07-25): operator identified as "Andrew Paris LLC,
  // d/b/a MediaShare-Link" (see tos v5 note). v2 -> v3 (2026-07-19): named
  // Google AdSense, "Guest Access and Agreement by Use", ad-supported
  // disclosure. v1 -> v2: Termly CMP disclosure. (Records which text a user
  // accepted; there is no enforced re-prompt flow.)
  privacy: 'v5',
  // Bumped v1 -> v2 (2026-07-25): issuer identified as "Andrew Paris LLC,
  // d/b/a MediaShare-Link" (see tos v5 note). Not a material change to AI
  // processing, so no re-prompt.
  ai: 'v2',
} as const;

export type ConsentKey = keyof typeof CONSENT_VERSIONS;

export interface ConsentRecord {
  readonly v: string;
  readonly at: string;
  readonly revoked?: string;
}

export type Consents = Partial<Record<ConsentKey, ConsentRecord>>;

/** Display metadata for each consent document, used by the Account UI. */
export const CONSENT_DOCS: ReadonlyArray<{
  readonly key: ConsentKey;
  readonly label: string;
  readonly path: string;
}> = [
  { key: 'tos', label: 'Terms of Service', path: '/legal/terms' },
  { key: 'privacy', label: 'Privacy Policy', path: '/legal/privacy' },
  { key: 'ai', label: 'AI Consent Notice', path: '/legal/ai-consent' },
];

export function buildConsentPayload(): Consents {
  const at = new Date().toISOString();
  return {
    tos: { v: CONSENT_VERSIONS.tos, at },
    privacy: { v: CONSENT_VERSIONS.privacy, at },
    ai: { v: CONSENT_VERSIONS.ai, at },
  };
}

/** Parse the Cognito `custom:consents` attribute, tolerating absence/garbage. */
export function parseConsents(raw: string | undefined | null): Consents {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/** Mark a document's consent as revoked, preserving the original version/date. */
export function revokeConsent(consents: Consents, key: ConsentKey): Consents {
  const existing = consents[key];
  const base: ConsentRecord = existing ?? {
    v: CONSENT_VERSIONS[key],
    at: new Date().toISOString(),
  };
  return { ...consents, [key]: { ...base, revoked: new Date().toISOString() } };
}

/** Re-grant a previously revoked document at the current version. */
export function grantConsent(consents: Consents, key: ConsentKey): Consents {
  return {
    ...consents,
    [key]: { v: CONSENT_VERSIONS[key], at: new Date().toISOString() },
  };
}
